import clsx from "clsx";
import { useReducer, useState } from "react";
import { useTranslation } from "react-i18next";

import { deleteBorder } from "@dashboard/shared/styles";
import { usePolicyCommands } from "@devices/components/Commands/hooks";
import { ConfirmDialog } from "@devices/components/shared";
import { MdmPolicyCommand } from "@devices/types";
import { Draggable, DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import { Box, Collapse, DialogContentText, IconButton, Typography, useTheme } from "@mui/material";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";

import { EditPolicyCommandDialog } from "./EditPolicyCommandDialog";
import { getPolicyCommandLabels } from "./policyCommandDisplay";
import { useStyles } from "./PolicyCommandItem.style";

interface PolicyCommandItemProps {
    policyCommand: MdmPolicyCommand;
    policyCommands: MdmPolicyCommand[];
    groupId: number;
    index: number;
}

const PolicyCommandItem = ({ policyCommand, policyCommands, groupId, index }: PolicyCommandItemProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels", "modals"]);
    const theme = useTheme();

    const [open, setOpen] = useState(-1);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const toggleModal = () => setIsDeleteModalOpen((prev) => !prev);

    const [editDialogOpen, setEditDialogOpen] = useReducer((state) => !state, false);

    const {
        clearPolicyCommands,
        loadingState: { updatePolicyLoading, clearPolicyLoading },
    } = usePolicyCommands();

    const handleDelete = async (policy: MdmPolicyCommand) => {
        const filteredPolicyCommands = policyCommands.filter((c) => c !== policy);
        await clearPolicyCommands({ groupId, policy: JSON.stringify(filteredPolicyCommands) });
    };

    const commandLabels = getPolicyCommandLabels(policyCommand);

    return (
        <>
            <Draggable draggableId={index.toString()} index={index} isDragDisabled={updatePolicyLoading}>
                {(draggableProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
                    return (
                        <>
                            <TableRow
                                key={index}
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                style={{
                                    ...draggableProvided.draggableProps.style,
                                    background: snapshot.isDragging ? "rgba(245,245,245, 0.75)" : "none",
                                }}
                            >
                                <TableCell align="left" style={deleteBorder(index, open)}>
                                    <Box style={{ display: "flex", alignItems: "center" }}>
                                        <IconButton
                                            className={classes.collapseBtn}
                                            onClick={() => setOpen(open === index ? -1 : index)}
                                            size="large"
                                        >
                                            {open === index ? (
                                                <i className="fa-solid fa-chevron-down" style={{ fontSize: 16 }} />
                                            ) : (
                                                <i className="fa-solid fa-chevron-right" style={{ fontSize: 16 }} />
                                            )}
                                        </IconButton>
                                        <div {...draggableProvided.dragHandleProps}>
                                            <i
                                                className="fas fa-bars"
                                                style={{ color: theme.palette.emteria.border.primary, fontSize: 16 }}
                                            />
                                        </div>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" width={20} style={deleteBorder(index, open)}>
                                    <Typography>{index + 1}</Typography>
                                </TableCell>
                                <TableCell align="left" style={deleteBorder(index, open)} className={clsx(classes.overflow)}>
                                    <Typography>{commandLabels.primary}</Typography>
                                    {commandLabels.secondary ? (
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            {commandLabels.secondary}
                                        </Typography>
                                    ) : null}
                                </TableCell>
                                <TableCell align="right" style={deleteBorder(index, open)}>
                                    <div className={classes.buttonBox}>
                                        <SecondaryButton
                                            tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.edit") }}
                                            onClick={setEditDialogOpen}
                                            className={updatePolicyLoading ? classes.iconDisabled : ""}
                                            loading={updatePolicyLoading}
                                            disabled={updatePolicyLoading}
                                        >
                                            <i className="fa-solid fa-fw fa-pencil" />
                                        </SecondaryButton>
                                        <DangerOutlinedButton
                                            onClick={() => setIsDeleteModalOpen(true)}
                                            tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.delete") }}
                                            loading={clearPolicyLoading}
                                            className={clearPolicyLoading ? classes.iconDisabled : ""}
                                            disabled={clearPolicyLoading}
                                        >
                                            <i className="fa-solid fa-fw fa-trash-alt" />
                                        </DangerOutlinedButton>
                                    </div>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className={clsx(classes.overflow, classes.customCollapse)}
                                    style={{ paddingTop: 0, paddingBottom: 0, border: "0px" }}
                                >
                                    <Collapse in={open === index} timeout="auto" unmountOnExit>
                                        <div className={classes.collapsingRowContent}>
                                            <br />
                                            <div>
                                                <pre>{JSON.stringify(policyCommand, null, 2)}</pre>
                                            </div>
                                        </div>
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        </>
                    );
                }}
            </Draggable>
            <EditPolicyCommandDialog
                groupId={groupId}
                policyCommands={policyCommands}
                policyCommand={policyCommand}
                isOpen={editDialogOpen}
                toggleDialog={setEditDialogOpen}
            />
            <ConfirmDialog
                title={t("modals:deleteCommand.title")}
                dangerButton
                primaryActionText={t("modals:deleteCommand.primaryActionText")}
                open={isDeleteModalOpen}
                onConfirm={() => handleDelete(policyCommand)}
                onClose={toggleModal}
            >
                <DialogContentText>{t("modals:deleteCommand.description")}</DialogContentText>
            </ConfirmDialog>
        </>
    );
};

export default PolicyCommandItem;
